import React from 'react';
import { CheckIcon, SwatchIcon } from '@heroicons/react/24/outline';
import {
  UiTheme,
  UI_THEME_PRESETS,
  normalizeUiTheme,
} from '../../lib/uiTheme';

interface UiThemeSettingsFormProps {
  theme: UiTheme;
  onChange: (theme: UiTheme) => void;
  disabled?: boolean;
}

const UiThemeSettingsForm: React.FC<UiThemeSettingsFormProps> = ({
  theme,
  onChange,
  disabled = false,
}) => {
  const current = normalizeUiTheme(theme);

  const selectPreset = (presetId: string, primaryHex: string) => {
    if (disabled) return;
    onChange(normalizeUiTheme({ presetId, primaryHex }));
  };

  const selectCustom = (hex: string) => {
    if (disabled) return;
    const primaryHex = hex?.toLowerCase?.() || '#4f46e5';
    onChange(normalizeUiTheme({ presetId: 'custom', primaryHex }));
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
          Temas predefinidos
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {UI_THEME_PRESETS.map((p) => {
            const active = current.presetId === p.id;
            return (
              <button
                key={p.id}
                type="button"
                disabled={disabled}
                onClick={() => selectPreset(p.id, p.primaryHex)}
                className={`flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left text-sm transition-all disabled:cursor-not-allowed ${
                  active
                    ? 'border-primary-400 bg-primary-50 ring-1 ring-primary-200'
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <span
                  className="h-6 w-6 shrink-0 rounded-full border-2 border-white shadow-sm ring-1 ring-slate-200"
                  style={{ background: p.primaryHex }}
                />
                <span className="min-w-0 flex-1 font-medium text-slate-800 truncate">{p.label}</span>
                {active && <CheckIcon className="h-4 w-4 shrink-0 text-primary-600" />}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
          Personalizado
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={disabled}
            onClick={() => selectCustom(current.primaryHex)}
            className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm transition-all disabled:cursor-not-allowed ${
              current.presetId === 'custom'
                ? 'border-primary-400 bg-primary-50 ring-1 ring-primary-200'
                : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
            }`}
          >
            <SwatchIcon className="h-4 w-4 text-slate-600" />
            Color libre
          </button>
          <input
            type="color"
            disabled={disabled}
            value={current.primaryHex}
            onChange={(e) => selectCustom(e.target.value)}
            className="h-9 w-9 cursor-pointer rounded-lg border border-slate-200 bg-white disabled:cursor-not-allowed"
            title="Color personalizado"
          />
          <input
            type="text"
            disabled={disabled}
            value={current.primaryHex}
            onChange={(e) => {
              const v = e.target.value.trim();
              if (/^#[0-9A-Fa-f]{6}$/.test(v)) selectCustom(v);
              else if (current.presetId === 'custom') {
                onChange({ presetId: 'custom', primaryHex: v.slice(0, 7) });
              }
            }}
            className="w-28 rounded-lg border border-slate-200 px-2.5 py-1.5 font-mono text-sm text-slate-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200 disabled:bg-slate-50 disabled:cursor-not-allowed"
            placeholder="#4f46e5"
            maxLength={7}
          />
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Vista previa</p>
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3">
          <button
            type="button"
            className="inline-flex items-center rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm"
          >
            Botón primario
          </button>
          <span className="inline-flex items-center rounded-md bg-primary-100 px-2 py-1 text-xs font-medium text-primary-800 ring-1 ring-primary-600/15">
            Chip activo
          </span>
          <span className="text-sm font-medium text-primary-600">Enlace de ejemplo</span>
          <span
            className="h-3 w-16 rounded-full"
            style={{ background: current.primaryHex }}
            title={current.primaryHex}
          />
        </div>
      </div>
    </div>
  );
};

export default UiThemeSettingsForm;
